import { Component } from '@theme/component';
import { debounce, onAnimationEnd, prefersReducedMotion } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import { RecentlyViewed } from '@theme/recently-viewed-products';
import { DialogCloseEvent, DialogOpenEvent, DialogComponent } from '@theme/dialog';
import { SearchUpdateEvent } from '@shopify/events';
import { loadSearchDictionary, loadSearchPins, pinnedQuery, rewriteQuery, searchPageUrl } from '@theme/snd-search-rewrite';

/**
 * A custom element that allows the user to search for resources available on the store.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchInput - The search input element.
 * @property {HTMLElement} predictiveSearchResults - The predictive search results container.
 * @property {HTMLElement} resetButton - The reset button element.
 * @property {HTMLElement[]} [resultsItems] - The search results items elements.
 * @property {HTMLElement} [recentlyViewedWrapper] - The recently viewed products wrapper.
 * @property {HTMLElement[]} [recentlyViewedTitle] - The recently viewed title elements.
 * @property {HTMLElement[]} [recentlyViewedItems] - The recently viewed product items.
 * @extends {Component<Refs>}
 */
class PredictiveSearchComponent extends Component {
  /** Predictive Search API maximum for `resources[limit]`. */
  static RESOURCE_LIMIT = 10;

  requiredRefs = ['searchInput', 'predictiveSearchResults', 'resetButton'];

  #controller = new AbortController();

  /**
   * @type {AbortController | null}
   */
  #activeFetch = null;

  #emptyStateLoaded = false;

  /**
   * Get the dialog component.
   * @returns {DialogComponent | null} The dialog component.
   */
  get dialog() {
    return this.closest('dialog-component');
  }

  connectedCallback() {
    super.connectedCallback();

    const { dialog } = this;
    const { signal } = this.#controller;

    if (this.refs.searchInput.value.length > 0) {
      this.#showResetButton();
    }

    // SND-19693: fetch the localized type dictionary before the first keystroke
    this.refs.searchInput.addEventListener(
      'focus',
      () => {
        loadSearchDictionary();
        loadSearchPins();
      },
      { signal, once: true }
    );
    // SND-20854: not `once` — a failed fetch is retried on the next focus (the promise guards duplicates)
    this.refs.searchInput.addEventListener('focus', () => this.#loadLazyEmptyState(), { signal });
    if (document.activeElement === this.refs.searchInput) this.#loadLazyEmptyState();

    // SND-20854: dialog (phone) search focuses its input on open; load there too.
    if (dialog) dialog.addEventListener(DialogOpenEvent.eventName, () => this.#loadLazyEmptyState(), { signal });

    if (dialog) {
      document.addEventListener('keydown', this.#handleKeyboardShortcut, { signal });
      dialog.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, { signal });
      dialog.addEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen, { signal, once: true });

      this.addEventListener('click', this.#handleModalClick, { signal });
    }

    if (RecentlyViewed.getProducts().length > 0) {
      requestIdleCallback(() => {
        this.#loadEmptyState();
      });
    }
  }

  /**
   * Handles clicks within the predictive search modal to maintain focus on the input
   * @param {MouseEvent} event - The mouse event
   */
  #handleModalClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const isInteractiveElement =
      target instanceof HTMLButtonElement ||
      target instanceof HTMLAnchorElement ||
      target instanceof HTMLInputElement ||
      target.closest('button') ||
      target.closest('a') ||
      target.closest('input');

    if (!isInteractiveElement && this.refs.searchInput) {
      this.refs.searchInput.focus();
    }
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  /**
   * Handles the CMD+K key combination.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyboardShortcut = (event) => {
    if (event.metaKey && event.key === 'k') {
      this.dialog?.toggleDialog();
    }
  };

  /**
   * Handles the dialog close event.
   */
  #handleDialogClose = () => {
    this.#resetSearch();
  };

  #handleDialogOpen = () => {
    if (!this.#emptyStateLoaded && RecentlyViewed.getProducts().length > 0) {
      this.#loadEmptyState();
    }
  };

  /**
   * SND-20854: the page ships a placeholder ([data-snd-sp-lazy]) instead of the empty-state
   * panel (~47 KB, 26 images, twice per page); it is fetched once, on the first focus /
   * dialog open. Never clears the input and never overwrites results the shopper already
   * typed for. Recently viewed (if any) still goes through #loadEmptyState on idle.
   */
  #lazyEmptyPromise = null;

  #loadLazyEmptyState() {
    const { predictiveSearchResults } = this.refs;
    if (this.#lazyEmptyPromise || !predictiveSearchResults?.querySelector('[data-snd-sp-lazy]')) return;
    const url = new URL(window.location.href);
    url.searchParams.delete('page');
    const sectionId = this.dataset.emptySectionId || 'predictive-search-empty';
    this.#lazyEmptyPromise = sectionRenderer
      .getSectionHTML(sectionId, true, url)
      .then((html) => {
        const parsed = new DOMParser()
          .parseFromString(html, 'text/html')
          .querySelector('.predictive-search-empty-section');
        const { searchInput } = this.refs;
        if (!parsed) {
          this.#lazyEmptyPromise = null;
          return;
        }
        if (searchInput?.value.trim()) return;
        if (!predictiveSearchResults.querySelector('[data-snd-sp-lazy]')) return;
        morph(predictiveSearchResults, parsed);
      })
      .catch(() => {
        this.#lazyEmptyPromise = null;
      });
  }

  #loadEmptyState() {
    if (this.#emptyStateLoaded) return;
    this.#emptyStateLoaded = true;
    this.resetSearch(false);
  }

  get #allResultsItems() {
    const containers = Array.from(
      this.querySelectorAll(
        '.predictive-search-results__wrapper-queries, ' +
          '.predictive-search-results__wrapper-products, ' +
          '.predictive-search-results__list'
      )
    );

    const allItems = containers
      .flatMap((container) => {
        if (container.classList.contains('predictive-search-results__wrapper-products')) {
          return Array.from(container.querySelectorAll('.predictive-search-results__card'));
        }
        return Array.from(container.querySelectorAll('[ref="resultsItems[]"], .predictive-search-results__card'));
      })
      .filter((item) => item instanceof HTMLElement);

    return /** @type {HTMLElement[]} */ (allItems);
  }

  /**
   * Track whether the last interaction was keyboard-based
   * @type {boolean}
   */
  #isKeyboardNavigation = false;

  get #currentIndex() {
    return this.#allResultsItems?.findIndex((item) => item.getAttribute('aria-selected') === 'true') ?? -1;
  }

  set #currentIndex(index) {
    if (!this.#allResultsItems?.length) return;

    let activeItem = null;

    this.#allResultsItems.forEach((item) => {
      item.classList.remove('keyboard-focus');
    });

    for (const [itemIndex, item] of this.#allResultsItems.entries()) {
      if (itemIndex === index) {
        item.setAttribute('aria-selected', 'true');
        if (this.#isKeyboardNavigation) {
          item.classList.add('keyboard-focus');
        }
        activeItem = item;
      } else {
        item.removeAttribute('aria-selected');
      }
    }

    activeItem?.scrollIntoView({ behavior: prefersReducedMotion() ? 'instant' : 'smooth', block: 'nearest' });
    this.refs.searchInput.focus();
  }

  get #currentItem() {
    return this.#allResultsItems?.[this.#currentIndex];
  }

  /**
   * Navigate through the predictive search results using arrow keys or close them with the Escape key.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  onSearchKeyDown = (event) => {
    if (event.key === 'Escape') {
      // SND-20229 header v2: Esc only closes the panel, the typed query stays
      if (this.closest('search-inline[data-hv2]')) return;
      this.#resetSearch();
      return;
    }

    if (!this.#allResultsItems?.length || event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      return;
    }

    const currentIndex = this.#currentIndex;
    const totalItems = this.#allResultsItems.length;

    switch (event.key) {
      case 'ArrowDown':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        break;

      case 'Tab':
        if (event.shiftKey) {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        } else {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        }
        break;

      case 'ArrowUp':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        break;

      case 'Enter': {
        const singleResultContainer = this.refs.predictiveSearchResults.querySelector('[data-single-result-url]');
        if (singleResultContainer instanceof HTMLElement && singleResultContainer.dataset.singleResultUrl) {
          event.preventDefault();
          window.location.href = singleResultContainer.dataset.singleResultUrl;
          return;
        }

        if (this.#currentIndex >= 0) {
          event.preventDefault();
          this.#currentItem?.querySelector('a')?.click();
        } else {
          event.preventDefault();
          window.location.href = searchPageUrl(this.refs.searchInput.value.trim()).toString();
        }
        break;
      }
    }
  };

  /**
   * Clears the recently viewed products.
   * @param {Event} event - The event.
   */
  clearRecentlyViewedProducts(event) {
    event.stopPropagation();

    RecentlyViewed.clearProducts();

    const { recentlyViewedItems, recentlyViewedTitle, recentlyViewedWrapper } = this.refs;

    const allRecentlyViewedElements = [...(recentlyViewedItems || []), ...(recentlyViewedTitle || [])];

    if (allRecentlyViewedElements.length === 0) {
      return;
    }

    if (recentlyViewedWrapper) {
      recentlyViewedWrapper.classList.add('removing');

      onAnimationEnd(recentlyViewedWrapper, () => {
        recentlyViewedWrapper.remove();
      });
    }
  }

  /**
   * Reset the search state.
   * @param {boolean} [keepFocus=true] - Whether to keep focus on input after reset
   */
  resetSearch = debounce((keepFocus = true) => {
    if (keepFocus) {
      this.refs.searchInput.focus();
    }
    this.#resetSearch();
  }, 100);

  /**
   * Debounce the search handler to fetch and display search results based on the input value.
   * Reset the current selection index and close results if the search term is empty.
   */
  search = debounce((event) => {
    // If the input is not a text input (like using the Escape key), don't search
    if (!event.inputType) return;

    const searchTerm = this.refs.searchInput.value.trim();
    this.#currentIndex = -1;

    if (!searchTerm.length) {
      this.#resetSearch();
      return;
    }

    this.#showResetButton();
    this.#getSearchResults(searchTerm);
  }, 200);

  /**
   * SND-16780: mirror the number of product suggestions into the "view all"
   * button ("Žiūrėti visus (10) →"). That button lives outside the morphed
   * results container, so the count travels as a data attribute in the markup.
   */
  #updateViewAllCount() {
    const button = this.refs.viewAllButton;
    if (!button) return;

    const marker = this.refs.predictiveSearchResults?.querySelector('[data-search-products-count]');
    const count = marker?.getAttribute('data-search-products-count');

    let slot = button.querySelector('[data-search-count]');

    if (!count) {
      slot?.remove();
      return;
    }

    if (!slot) {
      slot = document.createElement('span');
      slot.setAttribute('data-search-count', '');
      slot.className = 'predictive-search__result-count';
      const icon = button.querySelector('.svg-wrapper');
      icon ? button.insertBefore(slot, icon) : button.append(slot);
    }

    slot.textContent = `(${count})`;
  }

  /**
   * Resets scroll positions for search results containers
   */
  #resetScrollPositions() {
    requestAnimationFrame(() => {
      this.refs.predictiveSearchResults.querySelector('.predictive-search-results__inner')?.scrollTo(0, 0);
      this.querySelector('.predictive-search-form__content')?.scrollTo(0, 0);
    });
  }

  /**
   * Fetch search results using the section renderer and update the results container.
   * @param {string} searchTerm - The term to search for
   */
  async #getSearchResults(searchTerm) {
    if (!this.dataset.sectionId) return;

    const url = new URL(Theme.routes.predictive_search_url, location.origin);
    url.searchParams.set('q', searchTerm);
    url.searchParams.set('resources[limit_scope]', 'each');
    // SND-16780: without an explicit limit the section renderer returns a single
    // row of suggestions. 10 is the Predictive Search API maximum per resource.
    url.searchParams.set('resources[limit]', String(PredictiveSearchComponent.RESOURCE_LIMIT));

    const { predictiveSearchResults } = this.refs;

    const abortController = this.#createAbortController();

    const deferredPromise = SearchUpdateEvent.createPromise();

    this.dispatchEvent(
      new SearchUpdateEvent({
        search: {
          query: searchTerm,
        },
        promise: deferredPromise.promise,
      })
    );

    this.#getResultsMarkup(searchTerm, url)
      .then((resultsMarkup) => {
        if (!resultsMarkup) {
          deferredPromise.resolve({ totalCount: 0 });
          return;
        }

        if (abortController.signal.aborted) {
          deferredPromise.reject(new Error('Fetch aborted by user'));
          return;
        }

        morph(predictiveSearchResults, resultsMarkup);

        this.#updateViewAllCount();
        this.#resetScrollPositions();

        // Count all result items (products, collections, pages, articles, queries)
        const resultCount = predictiveSearchResults.querySelectorAll('[ref="resultsItems[]"]').length;
        deferredPromise.resolve({ totalCount: resultCount });
      })
      .catch((error) => {
        deferredPromise.reject(error);
        if (abortController.signal.aborted) return;
        throw error;
      });
  }

  /**
   * SND-19693: translated product types are not searchable, so a localized type
   * word ("patefonas") is rewritten to the EN type ("turntable") for products.
   * Queries and collections still come from what the customer typed (translated
   * collection titles are searchable), products from the rewritten term.
   * @param {string} searchTerm
   * @param {URL} url - the predictive search URL for the original term
   * @returns {Promise<string>}
   */
  async #getResultsMarkup(searchTerm, url) {
    const sectionId = /** @type {string} */ (this.dataset.sectionId);
    await Promise.all([loadSearchDictionary(), loadSearchPins()]);

    // SND-20463: a bare model name ("hd 25") is searched as the full product title
    // ("Sennheiser HD 25"), so the exact model is the best match, not a bundle or sibling.
    const productTerm = pinnedQuery(searchTerm) ?? rewriteQuery(searchTerm);
    if (productTerm === searchTerm) return sectionRenderer.getSectionHTML(sectionId, false, url);

    const productUrl = new URL(url);
    productUrl.searchParams.set('q', productTerm);
    productUrl.searchParams.set('resources[type]', 'product');

    const [markup, productMarkup] = await Promise.all([
      sectionRenderer.getSectionHTML(sectionId, false, url),
      sectionRenderer.getSectionHTML(sectionId, false, productUrl).catch(() => ''),
    ]);

    return PredictiveSearchComponent.mergeProductResults(markup, productMarkup);
  }

  /** Product blocks of the desktop panel (snd-search-panel) and the modal (predictive-search). */
  static PRODUCT_BLOCKS = '.snd-sp__area--t, .snd-sp__area--p, .predictive-search-results__products';

  /**
   * Replace the product blocks of `markup` with those of `productMarkup`.
   * @param {string} markup
   * @param {string} productMarkup
   * @returns {string}
   */
  static mergeProductResults(markup, productMarkup) {
    const parser = new DOMParser();
    const base = parser.parseFromString(markup, 'text/html');
    const products = parser.parseFromString(productMarkup, 'text/html');
    const blocks = [...products.querySelectorAll(PredictiveSearchComponent.PRODUCT_BLOCKS)];
    if (blocks.length === 0) return markup;

    const inner = base.querySelector('[data-search-results]') || base.querySelector('.predictive-search-results__inner');
    if (!inner) return productMarkup;

    // Nothing at all for the original term: the product-only render is the whole answer
    const grid = base.querySelector('.snd-sp__grid--results');
    const isPanel = Boolean(products.querySelector('.snd-sp__grid--results'));
    if (isPanel && !grid) return productMarkup;

    const container = isPanel ? grid : inner;
    const old = [...base.querySelectorAll(PredictiveSearchComponent.PRODUCT_BLOCKS)];
    // Where the products go: in place of the old ones, else before the collections
    // (panel) or right after the query pills (modal)
    let anchor = old[0] ?? null;
    if (!anchor && isPanel) anchor = container.querySelector(':scope > .snd-sp__area--c');
    if (!anchor && !isPanel) {
      const queries = container.querySelector(':scope > .predictive-search-results__wrapper-queries');
      anchor = queries ? queries.nextElementSibling : container.firstElementChild;
    }
    const parent = anchor?.parentNode ?? container;

    for (const block of blocks) parent.insertBefore(base.importNode(block, true), anchor);
    for (const block of old) block.remove();

    // Counters and shortcuts follow the products that are shown now
    base.querySelectorAll('.predictive-search-results__no-results, [data-single-result-url]').forEach((el) => el.remove());
    base.querySelector('[data-search-products-count]')?.remove();
    const count = products.querySelector('[data-search-products-count]');
    if (count) inner.append(base.importNode(count, true));

    return base.body.innerHTML;
  }

  /**
   * Fetch the markup for the recently viewed products.
   * @returns {Promise<string | null>} The markup for the recently viewed products.
   */
  async #getRecentlyViewedProductsMarkup() {
    if (!this.dataset.sectionId) return null;

    const viewedProducts = RecentlyViewed.getProducts();
    if (viewedProducts.length === 0) return null;

    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', viewedProducts.map(/** @param {string} id */ (id) => `id:${id}`).join(' OR '));
    url.searchParams.set('resources[type]', 'product');

    return sectionRenderer.getSectionHTML(this.dataset.sectionId, false, url);
  }

  #hideResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = true;
  }

  #showResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = false;
  }

  #createAbortController() {
    const abortController = new AbortController();
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }
    this.#activeFetch = abortController;
    return abortController;
  }

  #resetSearch = async () => {
    const { predictiveSearchResults, searchInput } = this.refs;
    // SND-17470: the desktop inline panel renders its own empty state (variant B).
    const emptySectionId = this.dataset.emptySectionId || 'predictive-search-empty';

    this.#currentIndex = -1;
    searchInput.value = '';
    this.#hideResetButton();

    const abortController = this.#createAbortController();
    const url = new URL(window.location.href);
    url.searchParams.delete('page');

    const emptySectionMarkup = await sectionRenderer.getSectionHTML(emptySectionId, false, url);
    const parsedEmptySectionMarkup = new DOMParser()
      .parseFromString(emptySectionMarkup, 'text/html')
      .querySelector('.predictive-search-empty-section');

    if (!parsedEmptySectionMarkup) throw new Error('No empty section markup found');

    /** This needs to be awaited and not .then so the DOM is already morphed
     * when #closeResults is called and therefore the height is animated */
    const viewedProducts = RecentlyViewed.getProducts();

    if (viewedProducts.length > 0) {
      const recentlyViewedMarkup = await this.#getRecentlyViewedProductsMarkup();
      if (!recentlyViewedMarkup) return;

      const parsedRecentlyViewedMarkup = new DOMParser().parseFromString(recentlyViewedMarkup, 'text/html');
      const recentlyViewedProductsHtml = parsedRecentlyViewedMarkup.getElementById('predictive-search-products');
      if (!recentlyViewedProductsHtml) return;

      for (const child of recentlyViewedProductsHtml.children) {
        if (child instanceof HTMLElement) {
          child.setAttribute('ref', 'recentlyViewedWrapper');
        }
      }

      const collectionElement = parsedEmptySectionMarkup.querySelector('#predictive-search-products');
      if (!collectionElement) return;
      collectionElement.prepend(...recentlyViewedProductsHtml.children);
    }

    if (abortController.signal.aborted) return;

    morph(predictiveSearchResults, parsedEmptySectionMarkup);
    this.#resetScrollPositions();
  };
}

if (!customElements.get('predictive-search-component')) {
  customElements.define('predictive-search-component', PredictiveSearchComponent);
}

